import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Shield, CheckCircle, Clock, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KYCAlertBanner = ({ userRole }) => {
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKYCStatus();
  }, []);

  const fetchKYCStatus = async () => {
    try {
      const token = localStorage.getItem('dockhta_token');
      const response = await fetch(`${API_URL}/api/kyc/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setKycStatus(data);
      }
    } catch (error) {
      console.error('Error fetching KYC status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  // If KYC is approved or verified by agent, don't show banner
  if (kycStatus?.status === 'approved' || kycStatus?.verified_by_agent) {
    return null;
  }

  // Different banners based on status
  const getBannerContent = () => {
    const status = kycStatus?.status || 'not_started';
    
    switch (status) {
      case 'not_started':
        return {
          bgColor: 'bg-amber-50 border-amber-200',
          iconColor: 'text-amber-600',
          textColor: 'text-amber-800',
          icon: AlertTriangle,
          title: 'Vérification requise',
          message: userRole === 'facility' 
            ? 'Pour activer votre établissement et apparaître dans l\'annuaire, veuillez soumettre vos documents légaux (Autorisation de création, Autorisation d\'exercice).'
            : 'Pour accepter des rendez-vous et apparaître dans les recherches, veuillez soumettre vos documents de vérification (Pièce d\'identité, Diplôme).',
          buttonText: 'Soumettre mes documents',
          buttonLink: '/kyc',
          blocking: true,
        };
      
      case 'pending':
        return {
          bgColor: 'bg-blue-50 border-blue-200',
          iconColor: 'text-blue-600',
          textColor: 'text-blue-800',
          icon: Clock,
          title: 'Vérification en attente',
          message: 'Vous avez commencé votre vérification. Certains documents sont encore manquants.',
          buttonText: 'Compléter ma vérification',
          buttonLink: '/kyc',
          blocking: true,
        };
      
      case 'submitted':
        return {
          bgColor: 'bg-blue-50 border-blue-200',
          iconColor: 'text-blue-600',
          textColor: 'text-blue-800',
          icon: Clock,
          title: 'Documents en cours de vérification',
          message: 'Vos documents ont été soumis et sont en cours d\'examen par notre équipe. Délai estimé : 24 à 48 heures.',
          buttonText: 'Voir le statut',
          buttonLink: '/kyc',
          blocking: false,
        };
      
      case 'rejected':
        return {
          bgColor: 'bg-red-50 border-red-200',
          iconColor: 'text-red-600',
          textColor: 'text-red-800',
          icon: AlertTriangle,
          title: 'Documents refusés',
          message: kycStatus?.rejection_reason 
            ? `Motif : ${kycStatus.rejection_reason}. Veuillez soumettre de nouveaux documents.`
            : 'Certains documents ont été refusés. Veuillez soumettre de nouveaux documents conformes.',
          buttonText: 'Soumettre à nouveau',
          buttonLink: '/kyc',
          blocking: true,
        };
      
      default:
        return null;
    }
  };

  const content = getBannerContent();
  
  if (!content) {
    return null;
  }

  const Icon = content.icon;

  return (
    <div 
      className={`mb-6 p-4 rounded-xl border-2 ${content.bgColor}`}
      data-testid="kyc-alert-banner"
    >
      <div className="flex items-start gap-4">
        <div className={`p-2 rounded-full ${content.bgColor}`}>
          <Icon className={`w-6 h-6 ${content.iconColor}`} />
        </div>
        <div className="flex-1">
          <h3 className={`font-semibold text-lg ${content.textColor}`}>
            {content.title}
          </h3>
          <p className={`mt-1 text-sm ${content.textColor} opacity-90`}>
            {content.message}
          </p>
          {content.blocking && (
            <div className="mt-2 p-2 bg-white/50 rounded-lg">
              <p className="text-xs font-medium text-slate-600">
                ⚠️ Certaines fonctionnalités sont limitées tant que votre compte n&apos;est pas vérifié :
              </p>
              <ul className="text-xs text-slate-500 mt-1 ml-4 list-disc">
                {userRole === 'facility' ? (
                  <>
                    <li>Votre établissement n&apos;apparaît pas dans l&apos;annuaire public</li>
                    <li>Vous ne pouvez pas recevoir de commandes ou ordonnances</li>
                    <li>Les professionnels ne peuvent pas s&apos;affilier à votre établissement</li>
                  </>
                ) : (
                  <>
                    <li>Vous n&apos;apparaissez pas dans les résultats de recherche</li>
                    <li>Vous ne pouvez pas accepter de nouveaux rendez-vous</li>
                    <li>Votre profil n&apos;est pas visible par les patients</li>
                  </>
                )}
              </ul>
            </div>
          )}
          <div className="mt-4">
            <Link to={content.buttonLink}>
              <Button 
                className={`${content.blocking ? 'bg-[#DC2626] hover:bg-[#B91C1C]' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                data-testid="kyc-banner-button"
              >
                <Shield className="w-4 h-4 mr-2" />
                {content.buttonText}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KYCAlertBanner;
